// Asset/manifest loader. Every visual has a programmatic canvas fallback, so a
// missing PNG is never fatal — the manifest just lets a real image be dropped in
// later. Keys: logo, fighter_idle, fighter_rock, fighter_paper, fighter_scissors,
// arena_bg (per the build brief) plus the per-class roster sheets
// public/assets/roster/<classId>_{idle,attack,special}.png (missing files
// degrade per-pose to the base fighter art + class tint in art/draw.ts).
//
// Per-class THROW sprites (public/assets/roster/<classId>_{talon,wing,beak}.png)
// are NOT part of the boot preload: loadThrows() fetches them for just the two
// classes in the current match (fight/online scene enter), and disposeThrows()
// drops them on scene exit so ten classes never sit in memory at once.

import manifest from "../assets/manifest.json";
import { ROSTER } from "../game/roster";
import { sheetExcluded } from "../art/roster-sheets";

export type AssetKey =
  | "logo"
  | "fighter_idle"
  | "fighter_rock"
  | "fighter_paper"
  | "fighter_scissors"
  | "arena_bg";

const KEYS: AssetKey[] = [
  "logo",
  "fighter_idle",
  "fighter_rock",
  "fighter_paper",
  "fighter_scissors",
  "arena_bg",
];

export type RosterPhase = "idle" | "attack" | "special";
const ROSTER_PHASES: RosterPhase[] = ["idle", "attack", "special"];

export type ThrowPhase = "talon" | "wing" | "beak";
const THROW_PHASES: ThrowPhase[] = ["talon", "wing", "beak"];

/**
 * Generated FX sprite ids (public/assets/fx/<id>.png) — the sprite layer of
 * the effect system (render/fx.ts). Loaded lazily with the fight assets via
 * loadFx() (createImageBitmap) and dropped on scene exit via disposeFx().
 * A missing file is never fatal: the FxSystem keeps its procedural particle
 * read as the fallback and the sprite simply doesn't blit.
 */
export const FX_SPRITE_IDS = [
  "fx_spur_slash",
  "fx_corn_shot",
  "fx_corn_burst",
  "fx_quill_volley",
  "fx_gaff_thrust",
  "fx_hit_spark",
  "fx_sun_beam",
  "fx_fireball",
  "fx_meteor",
  "fx_ice_shards",
  "fx_wind_blades",
  "fx_dust_puff",
  "fx_lightning",
  "fx_thunder_ring",
  "fx_shadow_rift",
  "fx_shadow_claw",
  "fx_ko_burst",
  "fx_buff_aura",
] as const;

export class Assets {
  private images = new Map<AssetKey, HTMLImageElement>();
  /** Per-class roster art, keyed `<classId>/<phase>`. */
  private roster = new Map<string, HTMLImageElement>();
  /** Class ids whose throw sprites were already requested (hit or miss). */
  private throwsRequested = new Set<string>();
  /** Decoded FX sprites (assets/fx/<id>.png), present only during a match. */
  private fxSprites = new Map<string, ImageBitmap>();
  private fxRequested = false;

  /** Attempts to load every manifested PNG; resolves regardless of failures. */
  async load(onProgress?: (frac: number) => void): Promise<void> {
    const paths = manifest as Partial<Record<AssetKey, string>>;
    const entries = KEYS.map((k) => [k, paths[k]] as const).filter(
      (e): e is [AssetKey, string] => typeof e[1] === "string" && e[1].length > 0,
    );
    // Fixed roster paths (not manifested): assets/roster/<id>_<phase>.png.
    // Deny-listed sheets (art/roster-sheets.ts) are never fetched — the
    // renderer would refuse to draw them anyway.
    const rosterEntries: [string, string][] = [];
    for (const f of ROSTER) {
      for (const ph of ROSTER_PHASES) {
        if (sheetExcluded(f.id, ph)) continue;
        rosterEntries.push([`${f.id}/${ph}`, `assets/roster/${f.id}_${ph}.png`]);
      }
    }
    let done = 0;
    const total = Math.max(entries.length + rosterEntries.length, 1);
    const loadOne = (path: string, store: (img: HTMLImageElement) => void): Promise<void> =>
      new Promise<void>((resolve) => {
        const img = new Image();
        // Lazy decode: fetch bytes now, decode off the critical path — the ten
        // roster classes' sheets never all block the main thread at boot. The
        // two classes actually fighting are pre-decoded via decodeRoster().
        img.decoding = "async";
        img.onload = () => {
          store(img);
          done++;
          onProgress?.(done / total);
          resolve();
        };
        img.onerror = () => {
          done++;
          onProgress?.(done / total);
          resolve();
        };
        // base:'./' — assets resolve relative to the document.
        img.src = path;
      });
    await Promise.all([
      ...entries.map(([key, path]) => loadOne(path, (img) => this.images.set(key, img))),
      ...rosterEntries.map(([key, path]) => loadOne(path, (img) => this.roster.set(key, img))),
    ]);
  }

  /**
   * Lazily loads the throw sprites (<id>_{talon,wing,beak}.png) for JUST the
   * given classes — call on match-scene enter with the two selected class ids.
   * Missing files resolve silently (draw.ts falls back to the class attack
   * sheet, then the base throw art). Deny-listed sheets (art/roster-sheets.ts)
   * are skipped — no point downloading PNGs the renderer refuses to draw.
   * Repeat calls for an already-requested class are no-ops.
   */
  async loadThrows(classIds: string[]): Promise<void> {
    const jobs: Promise<void>[] = [];
    for (const id of classIds) {
      if (this.throwsRequested.has(id)) continue;
      this.throwsRequested.add(id);
      for (const ph of THROW_PHASES) {
        if (sheetExcluded(id, ph)) continue;
        jobs.push(
          new Promise<void>((resolve) => {
            const img = new Image();
            img.decoding = "async";
            img.onload = () => {
              this.roster.set(`${id}/${ph}`, img);
              // Pre-decode: the reveal blits this sprite mid-chant — decoding
              // here keeps the first draw off the frame budget.
              img.decode?.().catch(() => undefined);
              resolve();
            };
            img.onerror = () => resolve();
            img.src = `assets/roster/${id}_${ph}.png`;
          }),
        );
      }
    }
    await Promise.all(jobs);
  }

  /**
   * Loads + decodes the generated FX sprites (createImageBitmap) — call
   * fire-and-forget from match-scene enter() alongside loadThrows(). Missing
   * or undecodable files resolve silently: render/fx.ts keeps its procedural
   * particle read as the fallback for any sprite that isn't in the map.
   * Repeat calls are no-ops until disposeFx().
   */
  async loadFx(): Promise<void> {
    if (this.fxRequested) return;
    this.fxRequested = true;
    await Promise.all(
      FX_SPRITE_IDS.map(async (id) => {
        try {
          const res = await fetch(`assets/fx/${id}.png`);
          if (!res.ok) return;
          const bmp = await createImageBitmap(await res.blob());
          this.fxSprites.set(id, bmp);
        } catch {
          /* missing file / decode failure → procedural fallback */
        }
      }),
    );
  }

  /** Decoded FX sprite for `id`, or null → procedural fallback. */
  getFx(id: string): ImageBitmap | null {
    return this.fxSprites.get(id) ?? null;
  }

  /** Closes + drops all FX bitmaps — call from match-scene dispose(). */
  disposeFx(): void {
    for (const b of this.fxSprites.values()) b.close();
    this.fxSprites.clear();
    this.fxRequested = false;
  }

  /** Drops all lazily-loaded throw sprites — call from match-scene dispose(). */
  disposeThrows(): void {
    for (const id of this.throwsRequested) {
      for (const ph of THROW_PHASES) this.roster.delete(`${id}/${ph}`);
    }
    this.throwsRequested.clear();
  }

  /** Returns the loaded image for `key`, or null to signal canvas fallback. */
  get(key: AssetKey): HTMLImageElement | null {
    return this.images.get(key) ?? null;
  }

  /** Per-class roster sheet, or null when that file is missing on disk. */
  getRoster(classId: string, phase: RosterPhase | ThrowPhase): HTMLImageElement | null {
    return this.roster.get(`${classId}/${phase}`) ?? null;
  }

  /**
   * Pre-decodes the roster sheets of the given classes (fire-and-forget from
   * match-scene enter): first-draw jank moves off the frame budget while the
   * other eight classes stay lazily decoded.
   */
  decodeRoster(classIds: string[]): void {
    for (const id of classIds) {
      for (const ph of ROSTER_PHASES) {
        this.roster.get(`${id}/${ph}`)?.decode?.().catch(() => undefined);
      }
    }
  }

  /** The manifest's audio map (sample key → path); empty when absent. */
  audioMap(): Record<string, string> {
    const audio = (manifest as { audio?: unknown }).audio;
    if (!audio || typeof audio !== "object") return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(audio as Record<string, unknown>)) {
      if (typeof v === "string" && v.length > 0) out[k] = v;
    }
    return out;
  }
}
